import { callWithNuxt } from '#app'

import type {
  PageBannerBackground,
  RootStoreState,
  TopNavBarItem,
  TripTypesSideBar,
  TripTypesSideBarContentCategoryTrip,
  AgentCookie,
  RolfsEvent,
  AgentCalls,
  PageBannerStars,
  PageBannerLink,
  PageBannerLinkButton,
  PageBannerType,
  PreviousRoute,
} from '@layers/web/stores/types/root.ts'
import type { Airport } from '@layers/web/stores/types/charter-packages.ts'
import type { Alert, HeroImagePage, InfoPage } from '@layers/web/services/types/content-api.ts'
// import type { BannerData } from '@layers/web/composables/types/banner.ts'
import type {
  EventRegistrationResponse,
  RegisterForEventParams,
  RolfsEventListItem,
} from '@layers/web/services/types/event-api.ts'
import { NavigationApiService } from '@layers/web/services/navigation-api.ts'
import { AgentApiService } from '@layers/web/services/agent-api.ts'
import { isUnauthorizedError } from '@layers/web/utils/error.ts'
import { EventApiService } from '@layers/web/services/event-api.ts'
import { ContentApiService } from '@layers/web/services/content-api.ts'
import { NewsletterApiService } from '@layers/web/services/newsletter-api.ts'
import { AGENT_CODE_CLEAN_TYPE } from '@layers/web/constants/api-fetch.ts'

const sidebarCategoryTripsRequests = new Map<string, Promise<void>>()

const useRootStore = defineStore('root', {
  state: (): RootStoreState => ({
    tripTypesSidebar: {
      toplinks: [],
      sections: [],
    },
    topNavbar: [],
    startCategories: [],
    pageBannerTitle: '',
    pageBannerTitleElement: '',
    pageBannerTitleI18n: '',
    pageBannerTitleDescription: '',
    pageBannerTitleDescriptionI18n: '',
    pageBannerSubtitle: '',
    pageBannerSubtitleI18n: '',
    pageBannerSubtitleColor: '',
    pageBannerSubtitleWeight: '',
    pageBannerType: null,
    pageMobileBannerType: null,
    pageBannerLink: null,
    pageBannerLinkButton: null,
    pageBannerLinkText: '',
    pageBannerLinkTextI18n: '',
    pageBannerSize: '',
    pageBannerBackground: null,
    pageBannerBackgroundClass: null,
    pageBannerStars: null,
    pageBannerPrice: null,
    pageBannerSpecial: null,
    bannerPriceWithoutDiscount: null,
    showNavigationArrow: false,
    // Available in: BannerCenteredText
    pageBannerShowSearchBar: false,
    pageSearchBarType: null,
    pageNavigationLink: null,
    pagePaddingBottomPx: null,
    pageBannerDays: null,

    pageGradientMask: true,
    pageExtraFooter: null,

    // Agents
    agentCookie: null,
    showAgentCalls: null,

    // For backend tracking users
    rolfsCookie: null,

    alert: {} as Alert,
    localUrl: null,

    selectedAirport: null,

    modalActive: false,

    startCalendar: [],
    newsletterAirports: [],
    event: null,
    previousRoutes: [],
  }),

  persist: [
    {
      paths: ['selectedAirport'],
      appendPathsToKey: true,
      storage: localPersistedState.localStorage,
    },
    {
      paths: ['rolfsCookie'],
      appendPathsToKey: true,
      storage: cookiesPersistedState.cookiesWithOptions({
        path: '/',
        maxAge: 60 * 60 * 24 * 365,
      }),
    },
    {
      paths: ['agentCookie'],
      key: 'a',
      serializer: {
        serialize: (value) => value.agentCookie
          ? JSON.stringify(value.agentCookie)
          : value.agentCookie,
        deserialize: (value) => ({ agentCookie: JSON.parse(value) }),
      },
      /**
       * Cookie on production domains should be available at the domain level
       * (e.g .rolfsbuss.se and not www.rolfsbuss.se)
       *
       * Domain is resolved lazily inside getItem/setItem since
       * useRuntimeConfig() requires an active Nuxt instance.
       */
      storage: {
        getItem (key, meta) {
          return cookiesPersistedState.cookiesWithOptions({
            path: '/',
            maxAge: 60 * 60 * 24 * 365,
            domain: getAgentCookieDomain(),
          }).getItem(key, meta)
        },
        setItem (key, value, meta) {
          cookiesPersistedState.cookiesWithOptions({
            path: '/',
            maxAge: 60 * 60 * 24 * 365,
            domain: getAgentCookieDomain(),
          }).setItem(key, value, meta)
        },
      },
    },
  ],

  getters: {
    previousRoute (state): PreviousRoute | null {
      return state.previousRoutes[0] || null
    },

    selectedAirportIata: (state) => state.selectedAirport?.iata || state.selectedAirport?.airport || null,

    showOfficeTools: (state) => !!state.agentCookie?.officetools,
    showAgentTools: (state) => !!state.agentCookie?.agenttools,
    showTranslationTool: (state) => !!state.agentCookie?.translationtools,
    cmsTools: (state) => !!state.agentCookie?.cmstools,
    isUserReseller (): boolean {
      return this.showAgentTools || this.showOfficeTools
    },

    showTranslationTools: (state) => {
      const localeStore = useLocaleStore()
      return !!state.agentCookie?.translationtools && localeStore.translationToolsActive
    },

    getOgImage (state) {
      const { imagesConfig } = useConfigStore()

      if (state.pageBannerBackground?.image?.url) {
        return {
          image: state.pageBannerBackground.image.url,
          type: state.pageBannerBackground.image.type,
        }
      }

      return {
        image: imagesConfig.fallbackHero.image,
        type: imagesConfig.fallbackHero.type,
      }
    },

    hasTripsForSidebarCategory: state => (categoryId: string): boolean => {
      const category = findSidebarCategory(state.tripTypesSidebar, categoryId)

      if (!category) {
        return false
      }

      return !!category.trips.length
    },

    getCategoryById: state => (id: string | number) => {
      const idString = String(id)

      const match = (state.tripTypesSidebar?.sections || [])
        .flatMap(section => section.categories.map(category => ({ section, category })))
        .find(({ category }) => String(category.id) === idString)

      if (!match) {
        return null
      }

      return {
        category: {
          name: match.category.name,
          url: match.category.url,
          name_translationkey: match.category.nameTranslationKey,
        },
        sectionName: match.section.name,
        fullKey: match.category.id,
      }
    },
  },

  actions: {
    // Simple setters
    selectAirport (airport: Airport | null) {
      this.selectedAirport = airport
    },

    setUrl (url: string) {
      this.localUrl = url
    },

    setTripTypes (types: TripTypesSideBar) {
      this.tripTypesSidebar = types
    },

    setTripsForSidebarCategory (categoryId: string, trips: TripTypesSideBarContentCategoryTrip[]) {
      const category = findSidebarCategory(this.tripTypesSidebar, categoryId)

      if (!category) {
        return
      }

      category.trips = trips
    },

    setTopNavbar (navbar: TopNavBarItem[]) {
      this.topNavbar = navbar
    },

    setPageBanner (data: any) {
      this.pageBannerTitle = data.title
      this.pageBannerTitleElement = data.titleElement
      this.pageBannerTitleI18n = data.titleI18n
      this.pageBannerTitleDescription = data.titleDescription
      this.pageBannerTitleDescriptionI18n = data.titleDescriptionI18n
      this.pageBannerSubtitle = data.subtitle
      this.pageBannerSubtitleI18n = data.subtitleI18n
      this.pageBannerSubtitleColor = data.subtitleColor
      this.pageBannerSubtitleWeight = data.subtitleWeight
      this.pageBannerBackground = data.background
      this.pageBannerBackgroundClass = data.backgroundClass
      this.pageBannerType = data.type
      this.pageMobileBannerType = data.mobileBannerType || data.mobileType
      this.pageBannerSize = data.size
      this.pageBannerLink = data.link
      this.pageBannerLinkButton = data.linkButton
      this.pageBannerLinkText = data.linkText
      this.pageBannerLinkTextI18n = data.linkTextI18n
      this.pageBannerStars = data.stars
      this.pageBannerPrice = data.price
      this.pageBannerDays = data.days
      this.pageBannerSpecial = data.special
      this.pageBannerShowSearchBar = data.showSearchBar
      this.pageSearchBarType = data.searchBarType ?? null
      this.bannerPriceWithoutDiscount = data.priceWithoutDiscount
      this.pagePaddingBottomPx = data.paddingBottomPx
      this.showNavigationArrow = data.showNavigationArrow
    },

    setPageBannerTitle (title: string) {
      this.pageBannerTitle = title
    },

    setPageBannerStars (stars: PageBannerStars | null) {
      this.pageBannerStars = stars
    },

    setPageBannerSubtitle (subtitle: string) {
      this.pageBannerSubtitle = subtitle
    },

    setPageBannerSubtitleColor (color: string) {
      this.pageBannerSubtitleColor = color
    },

    setPageBannerType (bannerType: PageBannerType) {
      this.pageBannerType = bannerType
    },

    setPageBannerBackground (data: PageBannerBackground) {
      this.pageBannerBackground = data
    },

    setPageBannerLink (link: PageBannerLink | null) {
      this.pageBannerLink = link
    },

    setPageBannerLinkButton (link: PageBannerLinkButton | null) {
      this.pageBannerLinkButton = link
    },

    setPageBannerDays (days: string | number) {
      this.pageBannerDays = days
    },

    setPageBannerPrice (price: number) {
      this.pageBannerPrice = price
    },

    setPageGradientMask (gradientMask: boolean) {
      this.pageGradientMask = gradientMask
    },

    setPageExtraFooter (extraFooter: string | null) {
      this.pageExtraFooter = extraFooter
    },

    setStartCategories (categories: string[]) {
      this.startCategories = categories
    },

    setAgentCookie (cookie: AgentCookie | null) {
      this.agentCookie = cookie
    },

    setShowAgentCalls (showAgentCalls: AgentCalls | null) {
      this.showAgentCalls = showAgentCalls
    },

    setRolfsCookie (cookie: string | null) {
      this.rolfsCookie = cookie
    },

    setAlert (alert: Alert) {
      this.alert = alert
    },

    setNewsletterAirports (airports: string[]) {
      this.newsletterAirports = airports
    },

    setEvent (event: RolfsEvent[] | null) {
      this.event = event
    },

    setModalActive (active: boolean) {
      this.modalActive = active
    },

    // Async actions
    async handleAgentCookie (agentCode?: string) {
      const isCleanCode = agentCode === AGENT_CODE_CLEAN_TYPE
      const hasExpiredCookie = !!this.agentCookie && '1752081984551' > this.agentCookie.currentDate // TODO: remove expiry check

      if (isCleanCode || hasExpiredCookie) {
        this.setAgentCookie(null)
        return
      }

      if (!agentCode) {
        return
      }

      try {
        const cookie = await AgentApiService.fetchResellerCookie(agentCode)
        this.setAgentCookie(cookie)
      } catch (error) {
        // A code the backend refuses is dropped, so a stale link leaves no agent cookie behind
        if (isUnauthorizedError(error)) {
          this.setAgentCookie(null)
        }

        throw error
      }
    },

    async fetchAgentCall () {
      this.setShowAgentCalls(null)

      if (!this.showOfficeTools) {
        return
      }

      const agentCalls = await AgentApiService.fetchCurrentAgentCalls()
      this.setShowAgentCalls(agentCalls ?? null)
    },

    async fetchStartCategories () {
      const startCategories = await NavigationApiService.fetchStartCategories()
      this.setStartCategories(startCategories)
    },

    async fetchTripTypesSidebar (): Promise<TripTypesSideBar> {
      const tripTypeSidebar = await NavigationApiService.fetchTripTypesSidebar()
      this.setTripTypes(tripTypeSidebar)
      return tripTypeSidebar
    },

    async fetchTripsForSidebarCategory (categoryId: string): Promise<void> {
      if (this.hasTripsForSidebarCategory(categoryId)) {
        return
      }

      if (!isNumericSidebarCategoryId(categoryId)) {
        return
      }

      const pendingRequest = sidebarCategoryTripsRequests.get(categoryId)

      if (pendingRequest) {
        await pendingRequest
        return
      }

      const request = NavigationApiService.fetchTripsForSidebarCategory(categoryId)
        .then((trips) => this.setTripsForSidebarCategory(categoryId, trips))
        .catch(() => undefined)
        .finally(() => sidebarCategoryTripsRequests.delete(categoryId))

      sidebarCategoryTripsRequests.set(categoryId, request)

      await request
    },

    async fetchTopNavbar (): Promise<TopNavBarItem[]> {
      const topNavbar = await NavigationApiService.fetchTopNavbar(!!this.agentCookie)
      this.setTopNavbar(topNavbar)
      return topNavbar
    },

    async fetchAlert () {
      const alert = await ContentApiService.fetchAlert()
      this.setAlert(alert)
      return alert
    },

    async fetchHeroImage ({ page, onlyFetch, nuxtApp }: { page: HeroImagePage; onlyFetch?: boolean; nuxtApp?: any }): Promise<PageBannerBackground['image']> {
      const { imagesConfig } = useConfigStore()
      const app = nuxtApp || useNuxtApp()

      const res = await callWithNuxt(app, () => ContentApiService.fetchHeroImage(page))

      // Weird logic for us to change the fallback image in frontend
      const isFallbackImage = res.picture_cloudinary === 'hero/rome_sqg4ot'

      const image: PageBannerBackground['image'] = isFallbackImage
        ? {
          url: imagesConfig.fallbackHero.image,
          type: imagesConfig.fallbackHero.type,
        }
        : {
          url: res.picture_cloudinary,
          type: 'cloudinary',
        }

      if (!onlyFetch) {
        this.setPageBannerBackground(image)
      }

      return image
    },

    async fetchNewsletterLists (): Promise<string[]> {
      const newsletterLists = await NewsletterApiService.fetchNewsletterLists()
      this.setNewsletterAirports(newsletterLists)
      return newsletterLists
    },

    async fetchSingleEvent ({ slug }: { slug: string }): Promise<RolfsEvent[]> {
      const event = await EventApiService.fetchSingleEvent(slug)
      this.setEvent(event)
      return event
    },

    async fetchUpcomingEvents (): Promise<RolfsEventListItem[]> {
      return await EventApiService.fetchUpcomingEvents()
    },

    async registerForEvent (params: RegisterForEventParams): Promise<EventRegistrationResponse | undefined> {
      return await EventApiService.registerForEvent(params)
    },

    async listAllInfoPages (): Promise<InfoPage[]> {
      return await ContentApiService.listAllInfoPages()
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useRootStore, import.meta.hot))
}

export { useRootStore }
